/*!
 * Copyright 2023 Quisitive Technologies, LLC. All rights reserved.
 */
import React, { useEffect, useState } from 'react';

import { get, values } from 'lodash';
import Element from '../../../../components/element';
import Flex from '../../../../components/flex';
import Text from '../../../../components/text';
import NoData from '../../NoData';
import { create, updateData } from '../../Chart/DistributionChart/chart';
import { createDonut, updateDonut } from '../../Chart/DonutChart/chart';

const BodyComposition = ({ healthReportData }) => {
  const [windowSize, setWindowSize] = useState([
    window.screen.availWidth,
    window.screen.availHeight,
  ]);

  useEffect(() => {

    // handle window resize
    const handleWindowResize = () => {
      setWindowSize([window.innerWidth, window.innerHeight]);
    }

    window.addEventListener('resize', handleWindowResize);
    const newBalanceList = values(healthReportData.body_composition);
    const donut_chart_data = healthReportData.body_composition;
    const newArray = [];

    newBalanceList.filter(each => {
      const keyId = each.display_label.replace(/ /g, '_').toLowerCase();
      const data = {
        ...each,
        section: 'body_composition',
        chartVal: each.value,
        key: keyId
      };
      if (
        (keyId === 'body_fat_mass' && each.unit === '%') ||
        (keyId === 'skeletal_muscle_mass' && each.unit === '%') ||
        keyId === 'visceral_fat_level'
      ) {
        newArray.push(data);
      }
    });
    newArray.map(each => {
      const settings = {
        el: each.display_label.replace(/ /g, '_') || 'none',
        margin: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0
        },
        transform: { x: 0, y: 18 }
      };
      create(settings);
      updateData(each);
    });

    const settingsDonut = {
      el: 'body_composition_chart',
      margin: { top: 0, right: 0, bottom: 0, left: 0 },
      palette: 'orange',
      path: '/station/charts/donut_chart'
    };
    const body_composition_data = {
      centerValue: {
        value:
          Number(donut_chart_data.weight.value).toFixed(2) +
          ' ' +
          donut_chart_data.weight.unit, // donut_chart_data.weight.value * 2.20462
        label: ''
      },
      chartData: [
        {
          legend: donut_chart_data.body_fat_mass.display_label,
          value:
            Number(donut_chart_data.percent_body_fat.value).toFixed(2) || '0',

          label:
            Number(donut_chart_data.body_fat_mass.value).toFixed(2) + // donut_chart_data.body_fat_mass.value * 2.20462
            ' ' +
            donut_chart_data.body_fat_mass.unit,
          fill: 'none',
          stroke: 'none'
        },
        {
          legend: donut_chart_data.skeletal_muscle_mass.display_label,
          value:
            Number(donut_chart_data.percent_skeletal_muscle.value).toFixed(2)
            || '0',

          // Number(donut_chart_data.percent_skeletal_muscle.value).toFixed(
          //   2
          // ),
          label:
            Number(donut_chart_data.skeletal_muscle_mass.value).toFixed(2) + // donut_chart_data.skeletal_muscle_mass.value * 2.20462
            ' ' +
            donut_chart_data.skeletal_muscle_mass.unit,
          fill: 'none',
          stroke: 'none'
        },

        {
          legend: donut_chart_data.other.display_label,
          value:
            Number(donut_chart_data.percent_other.value).toFixed(2) || '0',

          // Number(donut_chart_data.percent_other.value).toFixed(2),
          label:
            Number(donut_chart_data.other.value).toFixed(2) +
            ' ' +
            donut_chart_data.other.unit, // donut_chart_data.other.value * 2.20462
          fill: 'none',
          stroke: 'none'
        }
      ]
    };
    if (
      get(healthReportData, 'body_composition.percent_body_fat.value') !==
      null &&
      get(
        healthReportData,
        'body_composition.percent_skeletal_muscle.value'
      ) !== null &&
      get(healthReportData, 'body_composition.percent_other.value') !== null
    ) {
      createDonut(settingsDonut);
      updateDonut(body_composition_data);
    }

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    }
  }, []);
  return (
    <Element>
      {get(healthReportData, 'body_composition.percent_body_fat.value') ===
        null &&
        get(
          healthReportData,
          'body_composition.percent_skeletal_muscle.value'
        ) === null &&
        get(healthReportData, 'body_composition.percent_other.value') === null ? (
        <NoData />
      ) : (
        <Element>
          <Flex height={windowSize[1] <= 700 ? "450px" : "560px"} style={{ position: 'sticky' }}>
            <Element width="22%" marginRight="70px" marginTop="30px">
              <Element>
                <Text family="GTAM" size="14px" color="#222222">
                  Body composition analysis determines the percentages of fat,
                  muscle, water, and bone in a person's body. The ratio and
                  distribution of these components varies from person to person
                  and can have profound health implications.
                </Text>
              </Element>
            </Element>
            <div
              id="body_composition_chart"
              style={{ width: '70%', marginTop: '40px', marginBottom: '20px' }}
            ></div>
          </Flex>
          <Flex>
            <Element>
              <Flex align="baseline" marginTop="20px">
                <Element marginTop="40px" width="50%">
                  <Text family="GTAR" size="14px" color="#222222">
                    <strong>Body Fat Mass</strong> is the total weight of fat,
                    including surface (subcutaneous) fat and internal (visceral)
                    fat stored around abdominal organs. What is considered a
                    ‘healthy' amount of body fat differs based on age, gender, and
                    other factors.
                  </Text>
                </Element>
                <Element width="30%" marginRight="30px" marginLeft="36px">
                  <Text
                    family="GTAM"
                    size="16px"
                    color="#222222"
                    marginBottom="0px"
                  >
                    Body Fat Mass
                  </Text>
                </Element>
                <Element marginRight="35px">
                  <Text
                    family="GTAM"
                    size="24px"
                    color="#222222"
                    marginBottom="0px"
                  >
                    {
                      (Number(
                        get(
                          healthReportData,
                          'body_composition.percent_body_fat.value'
                        )
                      ).toFixed(2))
                      || '0'}
                    %
                    {/* {Number(
                      get(
                        healthReportData,
                        'body_composition.percent_body_fat.value'
                      )
                    ).toFixed(2)}
                    % */}
                  </Text>
                  <Element marginTop="3px">
                    <Text
                      family="GTAR"
                      size="14px"
                      color="#959595"
                      marginTop="0px"
                    >
                      Percentage
                    </Text>
                  </Element>
                </Element>
                <Element marginRight="50px" width="75px">
                  <Text
                    family="GTAM"
                    size="24px"
                    color="#222222"
                    marginBottom="0px"
                  >
                    {Math.floor(
                      Number(
                        get(
                          healthReportData,
                          'body_composition.percent_body_fat.percentile'
                        )
                      ) * 10
                    ) /
                      10 +
                      '%' || 0 + '%'}
                    {/* {Number(
                      get(
                        healthReportData,
                        'body_composition.percent_body_fat.percentile'
                      )
                    ).toFixed(2)}
                    % */}
                  </Text>
                  <Element marginTop="3px">
                    <Text
                      family="GTAR"
                      size="14px"
                      color="#959595"
                      marginTop="0px"
                    >
                      Percentile
                    </Text>
                  </Element>
                </Element>
                <div
                  id={get(
                    healthReportData,
                    'body_composition.percent_body_fat.display_label'
                  ).replace(/ /g, '_')}
                  style={{ width: '62%', marginBlock: "auto", marginTop: '26px' }}
                ></div>
              </Flex>
              <Flex align="baseline">
                <Element marginTop="40px" width="50%">
                  <Text family="GTAR" size="14px" color="#222222">
                    <strong>Skeletal Muscle Mass</strong> is the total weight of
                    muscles that can be grown and developed through exercise.
                  </Text>
                </Element>
                <Element width="30%" marginRight="30px" marginLeft="36px">
                  <Text
                    family="GTAM"
                    size="16px"
                    color="#222222"
                    marginBottom="0px"
                  >
                    Skeletal Muscle Mass
                  </Text>
                </Element>
                <Element marginRight="35px">
                  <Text
                    family="GTAM"
                    size="24px"
                    color="#222222"
                    marginBottom="0px"
                  >
                    {
                      (Number(
                        get(
                          healthReportData,
                          'body_composition.percent_skeletal_muscle.value'
                        )
                      ).toFixed(2))
                      || '0'}
                    %
                    {/* {Number(
                      get(
                        healthReportData,
                        'body_composition.percent_skeletal_muscle.value'
                      )
                    ).toFixed(2)}
                    % */}
                  </Text>
                  <Element marginTop="3px">
                    <Text
                      family="GTAR"
                      size="14px"
                      color="#959595"
                      marginTop="0px"
                    >
                      Percentage
                    </Text>
                  </Element>
                </Element>
                <Element marginRight="50px" width="75px">
                  <Text
                    family="GTAM"
                    size="24px"
                    color="#222222"
                    marginBottom="0px"
                  >
                    {Math.floor(
                      Number(
                        get(
                          healthReportData,
                          'body_composition.percent_skeletal_muscle.percentile'
                        )
                      ) * 10
                    ) /
                      10 +
                      '%' || 0 + '%'}
                    {/* {Number(
                      get(
                        healthReportData,
                        'body_composition.percent_skeletal_muscle.percentile'
                      )
                    ).toFixed(2)}
                    % */}
                  </Text>
                  <Element marginTop="3px">
                    <Text
                      family="GTAR"
                      size="14px"
                      color="#959595"
                      marginTop="0px"
                    >
                      Percentile
                    </Text>
                  </Element>
                </Element>
                <div
                  id={get(
                    healthReportData,
                    'body_composition.percent_skeletal_muscle.display_label'
                  ).replace(/ /g, '_')}
                  style={{ width: '62%', marginBlock: "auto", marginTop: '26px' }}
                ></div>
              </Flex>
              <Flex align="baseline">
                <Element marginTop="40px" width="50%">
                  <Text family="GTAR" size="14px" color="#222222">
                    <strong>Visceral Fat</strong> is internal abdominal fat
                    surrounding body organs. It is closely associated with
                    cardiovascular disease
                  </Text>
                </Element>
                <Element width="30%" marginRight="148px" marginLeft="36px">
                  <Text
                    family="GTAM"
                    size="16px"
                    color="#222222"
                    marginBottom="0px"
                  >
                    Visceral Fat Level
                  </Text>
                </Element>
                <Element marginRight="78px" width="75px">
                  <Text
                    family="GTAM"
                    size="24px"
                    color="#222222"
                    marginBottom="0px"
                  >
                    {get(
                      healthReportData,
                      'body_composition.visceral_fat_level.value'
                    )}
                  </Text>
                  <Element marginTop="3px">
                    <Text
                      family="GTAR"
                      size="14px"
                      color="#959595"
                      marginTop="0px"
                    >
                      Level
                    </Text>
                  </Element>
                </Element>
                <div
                  id={get(
                    healthReportData,
                    'body_composition.visceral_fat_level.display_label'
                  ).replace(/ /g, '_')}
                  style={{ width: '62%', marginBlock: "auto", marginTop: '26px' }}
                ></div>
              </Flex>
            </Element>
          </Flex>
        </Element>
      )}
    </Element>
  );
};

export default BodyComposition;
